import { useEffect, useMemo, type MutableRefObject } from 'react';
import { CheckoutLifecycleEventPostMessageName } from '@eventbrite/checkout-postmessage-api';
import { stageFromString } from '@eventbrite/webapp-constants';

import {
  getCheckoutMessageAllowedOrigins,
  isTrustedCheckoutPostMessageOrigin,
  parseCheckoutLifecycleEvent,
} from '@/src/listings/common/monitoring/checkout/checkout-post-message';
import { recordCheckoutClickToWidgetRender } from '@/src/listings/common/monitoring/rum-metrics';

export interface UseCheckoutLifecyclePostMessageParams {
  serverUrl: string;
  stage: string;
  eventId: string;
  enabled: boolean;
  interactionStartedAtRef?: MutableRefObject<number | null>;
}

export function useCheckoutLifecyclePostMessage({
  serverUrl,
  stage,
  eventId,
  enabled,
  interactionStartedAtRef,
}: UseCheckoutLifecyclePostMessageParams): void {
  const allowedOrigins = useMemo(
    () => getCheckoutMessageAllowedOrigins(serverUrl, stageFromString(stage)),
    [serverUrl, stage],
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handler = (event: MessageEvent): void => {
      if (!isTrustedCheckoutPostMessageOrigin(event.origin, allowedOrigins)) {
        return;
      }
      const eventName = parseCheckoutLifecycleEvent(event.data);
      if (eventName !== CheckoutLifecycleEventPostMessageName.widgetRenderComplete) {
        return;
      }

      if (interactionStartedAtRef?.current == null) {
        return;
      }

      const durationMs = Math.round(performance.now() - interactionStartedAtRef.current);
      interactionStartedAtRef.current = null;

      recordCheckoutClickToWidgetRender({
        durationMs,
        eventId,
        stage,
      });
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [allowedOrigins, eventId, enabled, stage, interactionStartedAtRef]);
}
